import { BottomBar } from './BottomBar';
import { SectionDate } from './SectionDate';
import { SectionLongText } from './SectionLongText';
import { SectionMultiRadio } from './SectionMultiRadio';
import { SectionProgramMulti } from './SectionProgramMulti';
import { SectionProgramYear } from './SectionProgramYear';
import { SectionProgramYearAmount } from './SectionProgramYearAmount';
import { SectionPrograms } from './SectionPrograms';
import { SectionProgramsYesNo } from './SectionProgramsYesNo';
import { SectionRadioTree } from './SectionRadioTree';
import { SectionYesNoDollars } from './SectionYesNoDollars';
import { SectionServiceCollection } from './ImpactSummary/SectionServiceCollection';
import { SectionService } from './ImpactSummary/SectionService';
import { SectionCheckboxes } from './SectionCheckboxes';
import { SectionNumber } from './SectionNumber';
import { SectionCustomOptions } from './SectionCustomOptions';
import React, {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cloneDeep, findIndex, noop, startCase } from 'lodash';
import { useClient, useNewNpoAnswer, useUpdateNpoAnswer } from 'shared/apollo';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import JSON5 from 'json5';

import Alert from '@mui/lab/Alert';
import Button from '@mui/material/Button';
import { Progress, Link } from '../styles';
import QuestionText from './QuestionText';
import {
  SECTION_FILTER,
  ENUM_TYPES,
  INTRO_SECTION_TEXT,
  getSection,
  FINAL_REPORT_SECTIONS,
  NOTE_QUESTION_IDS,
  NOTE_QUESTION_ID_LIST,
} from '../constants';
import { SectionBase } from './SectionBase';
import Skeleton from '@mui/lab/Skeleton';
import moment from 'moment';
import numeral from 'numeral';
import { useClearQuestionAnswers } from '../useQuestions';
import BeneficiaryLanding from './Beneficiary/BeneficiaryLanding';
import LocationLanding from './Location/LocationLanding';
import SectionMultiAddress from './Location/SectionMultiAddress';
import Intro from './Intro';
import SectionPeriod from './SectionPeriod';
import { formatDateForGraphQL, formattedDate } from 'shared/utils/date';
import SectionLocation from './Location/SectionLocation';
import { getTotalPercentage } from './Location/utils';
import ImpactSummaryIntro from './ImpactSummary/ImpactSummaryIntro';
import SectionIndicators from './SectionIndicators';
import SectionIndicatorDetails from './IndicatorDetails/SectionIndicatorDetails';
import Budget from './Budget';
import Narrative from './Narrative';
import SectionErrorBanner from './SectionErrorBanner';
import Review from './Review';
import ReportFinalizeSummary from './ReportFinalize/ReportFinalizeSummary';
import FinalizeBeneficiaries from './ReportFinalize/FinalizeBeneficiaries';
import FinalizeBudget from './ReportFinalize/FinalizeBudget';
import {
  isReportDatesPassed,
  mapIndicatorsById,
} from './IndicatorDetails/utils';
import { getImpactIndicators } from '../../NPOFeedback/MakeEdits/utils';
import { parsePeriodValuesString } from './SectionPeriod/utils';
import Activities from './Activities';
import { isCapacityDevelopmentEnabled } from './ImpactIndicator/utils';
import { labelToSectionId } from '../../NPOFeedback/utils';
import { addLastViewedSections } from 'shared/components/WelcomeBack/sectionLocalStorage';
import { DATES_QUESTION_ID_1 } from './ReportFinalize/answersUtil';
import { getGuidanceTextBySection } from './utils';
import {
  updateReportStateAndEndDates,
  updateReportLevel1DonorName,
} from './Review/api';
import OrgNameWarningModal from '../../ReportsBeta/LevelOneFeedback/ProgramNameWarningModal';
import { hasDonorName } from 'shared/utils/donorNameMatch';
import { DonorNameFlagProvider } from '../../ReportsBeta/LevelOneFeedback/DonorNameFlagProvider';
import { DONOR_NAME_FLAG_VALUE } from '../../ReportsBeta/LevelOneFeedback/donorNameFlagUtils';
import OverwriteProgramAnswerModal from '../../ReportsBeta/ProgramQuestions/OverwriteProgramAnswerModal';
import { useProgram } from 'shared/providers/ProgramProvider';
import { replaceQuestionText } from 'shared/utils/formatText';
import ImpactFullModel from './IndicatorDetails/IndicatorSummary/ImpactFullModel';
import ProgressNotes from './ReportFinalize/ProgressNotes';
import SectionNoteUpload from './SectionNoteUpload';

const checkBudgetBackNavigation = currentQuestionId => {
  switch (currentQuestionId) {
    case 80009:
      return 80005;
    case 80007:
      return 80003;
    case 80005:
      return 80007;

    default:
      return false;
  }
};

/*
  Base component/controller that renders and handles each question on specified section
  section: (string) current selected section
  onSectionEnd: (function) function to be triggered when a question tree reaches end.
  reportId. (int) id of the report begin build
  programName (string) used to be replaced in some question
  donationTotal (float) used to be replaced in some question
  sponsorName (string) used to be replaced in some question
  answers (array) existing answers on database
  onQuestionAnswered (function) to be executed when an answers is saved or updated
  onRequestBack: (function) to be triggered when pressing back on first question
  investmentId (int) id of the investment/donation managed with the allocation manager
  keepCached (bool) indicates to maintein the section controller state avalable when on other screen eg, summary, so history is keept
  onSummary (bool) indicates is summary is visible
*/

const sectionsFirstQuestionIds = [1, 10001, 30017];
const ignoreClearDataQuestionIds = [40004, 40006, 70001];

const showQuestionDetail =
  window.location?.hostname === 'localhost' &&
  import.meta.env.VITE_HIDE_QUESTION_DETAILS !== 'true';

const forcefullyTurnOffAutoSave = [20005, 20008, 20009];
export const SectionController = forwardRef((props, ref) => {
  const {
    sectionId,
    section,
    isDebug,
    customQuestions,
    onSectionEnd,
    report,
    reportId,
    programId,
    investmentId,
    programName,
    donationTotal,
    sponsorName,
    answers,
    loadingAnswers,
    baseQuestions,
    isInterimStage,
    isFinalizeStage,
    questions = [],
    onQuestionAnswered,
    onRequestBack,
    loadingNextQuestion,
    completedImpactSummary,
    feedbackType,
    accountName,
    serviceCollections,
    beneficiaries,
    beneficiariesLoading,
    beneficiariesError,
    beneficiariesRefetch,
    beneficiaryTags,
    indicators, // eslint-disable-line
    reportStartDate,
    reportEndDate,
    onQuestionChange = () => {}, // eslint-disable-line
    onNewAnswer = noop,
    keepCached = false,
    onSummary = false,
    reloadAnswers,
    reloadReport,
    isThirdParty,
    onNextQuestion = noop, // eslint-disable-line
    handleOpenImpactSummarySection = noop, // eslint-disable-line
    onError = noop, // eslint-disable-line
    handlePressOption,
    showSectionController,
  } = props;

  const client = useClient();
  const guidanceText = getGuidanceTextBySection(customQuestions);
  const { run: runNewAnswer, isLoading: loadingNew } = useNewNpoAnswer();
  const {
    run: runUpdateAnswer,
    isLoading: loadingUpdate,
  } = useUpdateNpoAnswer();
  const { run: runClear } = useClearQuestionAnswers();
  const [currentQuestion, setCurrentQuestion] = useState();
  const [error, setError0] = useState();
  const [values, setValues] = useState({});
  const [extraResourcesId, setExtraResourcesId] = useState({});
  const [autoSave, setAutoSave] = useState(false);
  const [dismissed, setDismissed] = useState([]); // eslint-disable-line
  const [programs, setPrograms] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [orgWarningOpen, setOrgWarningOpen] = useState(false);
  const [pendingNextArgs, setPendingNextArgs] = useState(null);
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const hasLevel1DonorNameRef = useRef(Boolean(report?.level1DonorName));
  const orgWarningResolveRef = useRef(null);

  const {
    answers: programAnswers,
    saveProgramAnswer,
    savingProgramAnswer,
  } = useProgram();

  // Snapshots the {questionId, programText, reportText, input, resume}
  // needed to render + resolve the "overwrite program answer" modal
  // after a report-level save on an `isProgramQuestion`. The transition
  // to the next question is deferred until the user picks skip / overwrite.
  const [pendingProgramOverwrite, setPendingProgramOverwrite] = useState(null);

  const isProgramLevelQuestion = currentQuestion?.isProgramQuestion;

  const donorNameAnswers = useMemo(
    () =>
      Object.entries(values).map(([id, value]) => ({
        questionId: Number(id),
        text: typeof value === 'string' ? value : JSON5.stringify(value),
      })),
    [values],
  );

  useEffect(() => {
    if (report?.level1DonorName) {
      hasLevel1DonorNameRef.current = true;
    }
  }, [report?.level1DonorName]);

  // For debugging
  if (isDebug) {
    window.getAnswers = () => values;
    window.getGuidanceText = () => guidanceText;
    window.getCustomQuestions = () => customQuestions;
    window.getQuestionId = () => currentQuestion?.dbId;
    window.getQuestion = () => currentQuestion;
    window.getReportAnswers = () => values;
    window.getReport = () => report;
  }

  const setError = useCallback(
    value => {
      setError0(value);
      onError(value);
    },
    [setError0, onError],
  );

  useEffect(() => {
    addLastViewedSections(report.dbId, labelToSectionId(section));
  }, [sectionId]);

  useEffect(() => {
    if (values) {
      onNewAnswer(values);
    }
  }, [onNewAnswer, values]);

  useEffect(() => {
    if (
      currentQuestion?.sectionStart &&
      currentQuestion?.enumPrograms &&
      programs.length === 0
    ) {
      setPrograms(currentQuestion?.enumPrograms || []);
    }
  }, [currentQuestion?.sectionStart]); // eslint-disable-line

  const crrtValue = values?.[currentQuestion?.dbId];
  const optionalQuestion = useMemo(
    () => currentQuestion?.enumResponses?.[0]?.responseType === ENUM_TYPES.FILE,
    [currentQuestion],
  );

  let choiceIsSelected = Boolean(
    crrtValue?.option ||
      typeof crrtValue === 'string' ||
      crrtValue?.date ||
      (crrtValue?.value && crrtValue?.subValue) ||
      (crrtValue?.address && crrtValue?.longitude && crrtValue?.latitude) ||
      crrtValue?.value === 'Other' ||
      (Array.isArray(crrtValue) && crrtValue.length > 0) ||
      currentQuestion?.questionType === 'INTRO' ||
      (crrtValue?.answerId && crrtValue?.text) ||
      optionalQuestion ||
      currentQuestion?.dbId === 50007 ||
      (currentQuestion?.dbId === 50028 && crrtValue),
  );
  if (currentQuestion?.dbId === 40010) {
    choiceIsSelected = crrtValue
      ? typeof crrtValue === 'string'
        ? crrtValue.length > 2
        : crrtValue.length > 0
      : false;
  }
  if (currentQuestion?.dbId === 70001) {
    choiceIsSelected =
      typeof crrtValue === 'string' ||
      !!(crrtValue?.text?.text && crrtValue?.text?.url);
  }
  if (currentQuestion?.dbId === 70002) {
    choiceIsSelected =
      typeof crrtValue === 'string' ||
      !!(crrtValue?.text?.text && crrtValue?.text?.lesson);
  }
  if (currentQuestion?.dbId === 80003) {
    choiceIsSelected =
      crrtValue?.text?.text &&
      crrtValue?.text?.currency &&
      parseFloat(crrtValue?.text?.currency ?? 0) > 0;
  }
  if (currentQuestion?.dbId === 40021) {
    choiceIsSelected = Boolean(crrtValue);
  }

  let sum;
  if (currentQuestion?.dbId === 20007) {
    sum = (crrtValue || []).reduce((acc, itm) => {
      acc += itm.value;
      return acc;
    }, 0);
    choiceIsSelected = sum === donationTotal;
  }

  if (currentQuestion?.dbId === 80005 || currentQuestion?.dbId === 80007) {
    choiceIsSelected =
      crrtValue && crrtValue?.text?.currency && crrtValue?.text?.text;
  }

  if (currentQuestion?.dbId === 80009) {
    choiceIsSelected =
      crrtValue && crrtValue?.text?.url && crrtValue?.text?.text;
  }

  /* allocation wizard sum validation */
  useEffect(() => {
    if (sum && !choiceIsSelected && currentQuestion?.dbId === 20007) {
      setError([
        {
          text: (
            <span>
              Sum of all donation is expected to be&nbsp;
              <b>${numeral(donationTotal).format(0, 0)}</b>, but it's{' '}
              <b>${numeral(sum ?? 0).format(0, 0)}</b>
            </span>
          ),
        },
      ]);
    }
  }, [choiceIsSelected, currentQuestion?.dbId, sum]);

  /* Exposes loadQuestion method so it can be used from the summary to call a question from edit/pencil */
  useImperativeHandle(ref, () => ({
    loadQuestion(id) {
      //      id = 20016
      const visibleQuestion = questions.find(({ dbId }) => dbId === id);
      if (visibleQuestion) {
        if (selectedPrograms) {
          visibleQuestion['enumPrograms'] = selectedPrograms;
        }
        onQuestionChange(visibleQuestion);
        setCurrentQuestion(visibleQuestion);
      } else {
        console.error(`ERROR! Question #${id} not found!`);
      }
      /*
        INFO: Had to comment this out as it was produccing
        unexpected behaviours runing validations when not needed
      */
      /*if (onSetHandleNext) {
        onSetHandleNext(handleNext)
      }*/
    },
    // Exposes a method that lets the parent component run the save method for questions
    triggerNext() {
      if (currentQuestion.dbId === 80009) {
        return;
      }
      handleNext();
    },
    getCurrentData() {
      return {
        currentQuestion,
        crrtValue,
      };
    },
  }));

  /* this effect loads current answers from database */
  useEffect(() => {
    if (answers && answers.length > 0) {
      const currentAnswers = cloneDeep(values);
      answers.forEach(ans => {
        switch (ans.question.questionType) {
          case 'RADIO':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'MIXED':
            if (ans.enumResponse?.responseType === 'period') {
              currentAnswers[ans.questionId] = {
                option: ans.text,
                startDate: ans.date1,
                endDate: ans.date2,
              };
            } else if (ans.enumResponse?.responseType === 'date') {
              currentAnswers[ans.questionId] = {
                option: ans.text,
                startDate: ans.date1,
              };
            } else {
              currentAnswers[ans.questionId] = {
                option: ans.enumResponse?.response,
              };
            }
            break;
          case 'DATE':
            currentAnswers[ans.questionId] = { date: ans.date1 };
            break;
          case 'PERIOD':
            currentAnswers[ans.questionId] = {
              startDate: ans.date1,
              endDate: ans.date2,
            };
            break;
          case 'LONG_TEXT':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'MULTIADDRESS':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'SERVICE_COLLECTION':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'SERVICE':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'BENEFICIARIES':
            currentAnswers[ans.questionId] = ans.text;
            break;
          case 'CUSTOM_OPTIONS': {
            let customOptVal = ans.text;
            try {
              const jsonResponse = JSON5.parse(ans.text.replaceAll('\'', '"'));
              customOptVal = {
                text: jsonResponse,
                answerId: ans?.enumResponse?.dbId,
                type: ans?.enumResponse?.responseType,
              };
            } catch (err) {
              console.warn(err);
            }
            currentAnswers[ans.questionId] = customOptVal;
            break;
          }
          default:
            currentAnswers[ans.questionId] = ans.text;
            break;
        }
      });
      setValues(currentAnswers);
    }
    /* do not include values on dependencies as values is mutated within and produces an infinite loop */
  }, [answers]); // eslint-disable-line

  /* Handles when an answer if changed from components */
  const handleValueChange = useCallback(
    (id, value, resourceId) => {
      if (resourceId) {
        setExtraResourcesId(oldValues => ({
          ...oldValues,
          [id]: resourceId,
        }));
      }
      setValues(oldValues => ({ ...oldValues, [id]: value }));
      if (
        currentQuestion?.questionType === 'CUSTOM_OPTIONS' &&
        value?.type === 'link'
      ) {
        setAutoSave(true);
      }
      if (section === 'Budget') {
        setAutoSave(true);
      }
      if (section === 'Narrative') {
        setAutoSave(true);
      }
      if (section === 'indicator_details') {
        setAutoSave(false);
      }
    },
    [beneficiaryTags, currentQuestion],
  );
  /*
    Listens for answer selection, if autosave is enabled process the answer
    autoSave: avoids to proccess on values changing when open a section
  */
  useEffect(() => {
    if (
      autoSave &&
      !forcefullyTurnOffAutoSave.includes(currentQuestion?.dbId)
    ) {
      handleNext();
    }
    setAutoSave(false);
  }, [values, autoSave]); // eslint-disable-line

  const processForm = useCallback(
    (idx, forms, onComplete) => {
      const lastAnsIdx = forms.length;
      runNewAnswer(forms[idx], (_newId, nextQuestion) => {
        const nextForm = idx + 1;
        if (nextForm === lastAnsIdx) {
          onComplete(nextQuestion);
        } else {
          processForm(nextForm, forms, onComplete);
        }
      });
    },
    [runNewAnswer],
  );

  /* Handle next question got from mutation response on save or update */
  const handleNextQuestion = useCallback(
    nextQuestion => {
      const answered = currentQuestion;
      // Extracted so we can defer the transition behind the
      // overwrite-program-answer modal without duplicating the wiring.
      const applyTransition = () => {
        if (nextQuestion?.dbId) {
          onQuestionChange(nextQuestion);
          setCurrentQuestion(nextQuestion);
          setSelectedPrograms(nextQuestion?.enumPrograms || []);
        } else {
          // Special case, is a table which actually is a summary already, should skip general section summary.
          const currentSection = answered?.section;
          const sectionsWithNoSummary = [
            'description',
            // 'allocation',
            'dates',
            'activities',
            'location',
            'impact_summary',
            'indicator_details',
            'narrative',
            'budget',
          ];
          const skipToNextSection =
            sectionsWithNoSummary.indexOf(currentSection) > -1;
          onSectionEnd(skipToNextSection);
        }
      };

      // If the just-saved report answer diverges from the program-level
      // answer for the same question, pop the overwrite modal before
      // moving on. Skip / Overwrite both call `applyTransition` — the
      // report save is already done at this point.
      if (answered?.isProgramQuestion && saveProgramAnswer) {
        const raw = values[answered.dbId];
        const reportText = (typeof raw === 'string'
          ? raw
          : raw != null
          ? JSON5.stringify(raw)
          : ''
        ).trim();
        const programText = (
          programAnswers?.[answered.dbId]?.text ?? ''
        ).trim();
        if (programText && reportText && programText !== reportText) {
          const questionTitle = replaceQuestionText({
            text: answered.text ?? String(answered.dbId),
            programName,
            donationTotalDisplay: donationTotal,
            sponsorName,
          });
          setPendingProgramOverwrite({
            questionId: answered.dbId,
            questionTitle,
            programText,
            reportText,
            input: { text: reportText },
            resume: () => {
              setPendingProgramOverwrite(null);
              applyTransition();
            },
          });
          return;
        }
      }

      applyTransition();
    },
    [
      onQuestionChange,
      onSectionEnd,
      programAnswers,
      saveProgramAnswer,
      values,
      donationTotal,
      programName,
      sponsorName,
    ],
  );

  const handleSkipProgramOverwrite = useCallback(() => {
    const p = pendingProgramOverwrite;
    if (!p) return;
    p.resume();
  }, [pendingProgramOverwrite]);

  const handleConfirmProgramOverwrite = useCallback(async () => {
    const p = pendingProgramOverwrite;
    if (!p || !saveProgramAnswer) return;
    const res = await saveProgramAnswer(p.questionId, p.input);
    if (!res.success) {
      // Keep modal open; savingProgramAnswer surfaces the error inline.
      return;
    }
    p.resume();
  }, [pendingProgramOverwrite, saveProgramAnswer]);

  /* Handle multi reponses, for example multiple programs, this is an new commmon operation on allocation manager */
  const handleMultipleAnswers = useCallback(
    selection => {
      let forms = [];
      if (
        currentQuestion.questionType === 'PROGRAM' ||
        currentQuestion.questionType === 'PROGRAM_CORRECT_TYPE'
      ) {
        forms = selection.map(sel => {
          const form = {
            investmentId,
            questionId: currentQuestion.dbId,
            questionText: currentQuestion.text,
            text:
              (currentQuestion?.enumPrograms || []).filter(
                pr => pr.id === sel,
              )?.[0]?.name || '',
          };
          if (currentQuestion.questionType === 'PROGRAM') {
            const isNew = typeof sel === 'string';
            form.programId = isNew ? undefined : sel;
            form.text = isNew
              ? sel
              : (currentQuestion?.enumPrograms || []).filter(
                  pr => pr.id === sel,
                )?.[0]?.name || '';
          }
          if (currentQuestion.questionType === 'PROGRAM_CORRECT_TYPE') {
            form.programId = parseInt(sel.id);
            form.text = sel.value;
          }
          return form;
        });
      }
      if (currentQuestion.questionType === 'PROGRAM_YEAR') {
        selection.forEach(sel => {
          if (typeof sel.value === 'string' || typeof sel.value === 'number') {
            forms.push({
              investmentId,
              questionId: currentQuestion.dbId,
              questionText: currentQuestion.text,
              text: sel.value,
              programId: parseInt(sel.id),
            });
          } else if (Array.isArray(sel.value)) {
            sel.value.forEach(rptId => {
              forms.push({
                investmentId,
                questionId: currentQuestion.dbId,
                questionText: currentQuestion.text,
                text: '',
                programId: parseInt(sel.id),
                reportId: rptId,
              });
            });
          }
        });
      }
      if (currentQuestion.questionType === 'PROGRAM_MULTI') {
        selection.forEach(sel => {
          forms.push({
            investmentId,
            questionId: currentQuestion.dbId,
            questionText: currentQuestion.text,
            text: sel.value.text,
            programId: parseInt(sel.id),
            reportId: sel.value.report,
            answerId: sel.value.answer,
          });
        });
      }
      runClear({ investmentId, questionId: currentQuestion.dbId }, () => {
        // Once old choices are cleared, save new multy answers.
        processForm(0, forms, nextQuestion => {
          handleNextQuestion(nextQuestion);
          onQuestionAnswered();
        });
      });
    },
    [
      currentQuestion?.dbId,
      currentQuestion?.enumPrograms,
      currentQuestion?.questionType,
      currentQuestion?.text,
      handleNextQuestion,
      investmentId,
      onQuestionAnswered,
      processForm,
      runClear,
    ],
  ); // eslint-disable-line

  // Saves or updates current question answer.
  const saveOrUpdateAnswer = useCallback(
    formAnswer => {
      if (formAnswer.id) {
        runUpdateAnswer(formAnswer, (newId, nextQuestion) => {
          handleNextQuestion(nextQuestion);
          onQuestionAnswered();
        });
      } else {
        runNewAnswer(formAnswer, (newId, nextQuestion) => {
          // This code was incorrectly executed here, leading to duplicated indicators on custom selection.
          // See report TIPD-2684 for reproduction.
          /*if (formAnswer.questionId === 40010) {
            addCustomIndicator(formAnswer);
          }*/
          handleNextQuestion(nextQuestion);
          onQuestionAnswered();
        });
      }
    },
    [
      onQuestionAnswered,
      runNewAnswer,
      runUpdateAnswer,
      handleNextQuestion,
      answers,
    ],
  );

  const isEmptyOrSpaces = str => {
    return str === null || str.match(/^ *$/) !== null;
  };

  /*
    This methods evals a lot of conditions as it process every time the user press on save & continue,
    so, the method must check the question and answer type, validate them according, check if there
    are nextQuestionId and submit to graphql, saving or updating the resource.
  */
  const validate = useCallback(
    (answer, validations) => {
      if (!answer && validations.length === 0) {
        return [];
      }
      if (
        answer.value === 'Other' &&
        answer.subValue === 'Other' &&
        answer.startDate &&
        answer.endDate === undefined
      ) {
        return [
          {
            id: null, // catch the end date error in the frontend
            type: 'error',
            text: 'Please provide the end date as well.',
          },
        ];
      }
      const issues = [];
      (validations || []).forEach(val => {
        if (dismissed.indexOf(val.dbId) > -1) {
          return;
        }
        switch (val.validation) {
          case 'no_input_text':
            if (!answer.text || answer.text === '') {
              issues.push({
                type: val.validationType,
                text: val.text,
                id: val.dbId,
              });
            }
            break;
          case 'no_selection':
            if (!answer.text || answer.text === '') {
              issues.push({
                type: val.validationType,
                text: val.text,
                id: val.dbId,
              });
            }
            break;
          case 'dates_more_than_year':
            if (answer.date1 && answer.date2) {
              const start = moment(answer.date1, 'YYYY-MM-DD');
              const end = moment(answer.date2, 'YYYY-MM-DD');
              if (end.diff(start, 'years', true) > 1) {
                issues.push({
                  type: val.validationType,
                  text: val.text,
                  id: val.dbId,
                });
              }
            }
            break;
          case 'date_end_before_start':
            if (answer.date1 && answer.date2) {
              const start = moment(answer.date1, 'YYYY-MM-DD');
              const end = moment(answer.date2, 'YYYY-MM-DD');
              if (end.isBefore(start)) {
                issues.push({
                  type: val.validationType,
                  text: val.text,
                  id: val.dbId,
                });
              }
            }
            break;
          case '10_chars_required':
            if (!answer.text || answer.text.length <= 10) {
              issues.push({
                type: val.validationType,
                text: val.text,
                id: val.dbId,
              });
            }
            break;
          default:
            break;
        }
      });
      return issues;
    },
    [dismissed],
  );

  const handleNext = useCallback(
    (pressedButton = false, forcedValues = null) => {
      const questionId =
        currentQuestion?.dbId ||
        (section === 'Impact Model Details' ? 50001 : currentQuestion?.dbId);

      /* TEMPORAL: if there are no questions, continue */
      if (!questions || questions.length === 0) {
        onSectionEnd();
        return;
      }
      let response;
      const extraDates = {};
      let value = values[currentQuestion?.dbId];
      const valueStr =
        typeof value === 'string' ? value : JSON5.stringify(value);
      if (
        levelOneFeedbackEnabled &&
        !hasLevel1DonorNameRef.current &&
        valueStr &&
        report.sponsorName &&
        hasDonorName(valueStr, report.sponsorName)
      ) {
        setPendingNextArgs({ pressedButton, forcedValues, value });
        setOrgWarningOpen(true);
        return;
      }
      const validations =
        questions.filter(q => q.dbId === questionId)?.[0]?.validations ||
        currentQuestion?.validations ||
        [];
      const errors = validate(value, validations) || [];
      if (pressedButton) {
        // fix for measurement
        if (
          currentQuestion?.enumResponses?.[0]?.responseType === 'measured' &&
          value === 'link'
        ) {
          setError([...errors, { text: 'Please provide a value to continue' }]);
          return;
        }
      }
      let qType = currentQuestion?.questionType;
      /* detect the response object according to the question type*/
      switch (qType) {
        case 'RADIO':
          response = currentQuestion.enumResponses?.find(
            ({ response }) => response === value,
          );
          break;
        case 'DATE':
          if (!values[currentQuestion.dbId]) {
            setError([...errors, { text: 'Please provide a date' }]);
            return;
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: moment(value.date).format('YYYY-MM-DD'),
          };
          break;
        case 'PERIOD':
          if (!value?.endDate || !value?.startDate) {
            setError([
              ...errors,
              { text: 'Please complete the dates to continue.' },
            ]);
            return;
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: `${moment(value.startDate).format(
              'YYYY-MM-DD',
            )} to ${moment(value.endDate).format('YYYY-MM-DD')}`,
          };
          break;
        case 'LONG_TEXT':
          if (!value) {
            setError([
              ...errors,
              { text: 'Please answer this question to continue' },
            ]);
            return;
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: value,
          };
          break;
        case 'PROGRAM_YEAR_AMOUNT':
          if (Array.isArray(value)) {
            const sum = value?.reduce((all, { value }) => all + value * 1, 0);
            if (sum !== donationTotal && sum) {
              setError([
                ...errors,
                {
                  text: (
                    <span>
                      Sum of all donation is expected to be&nbsp;
                      <b>${numeral(donationTotal).format(0, 0)}</b>, but it's{' '}
                      <b>${numeral(sum ?? 0).format(0, 0)}</b>
                    </span>
                  ),
                },
              ]);
              return;
            }
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: value,
          };
          break;
        case 'TREE':
          if (value.value === 'Calendar Year') {
            response = currentQuestion.enumResponses
              .slice(0, 4)
              .find(({ response }) => response === value.subValue);
            if (value.subValue === 'Other' && value?.startDate)
              extraDates.date1 = moment(value.startDate).format('YYYY-MM-DD');
          }
          if (value.value === 'Fiscal Year') {
            response = currentQuestion.enumResponses
              .slice(4, 8)
              .find(({ response }) => response === value.subValue);
            if (value.subValue === 'Other' && value?.startDate)
              extraDates.date1 = moment(value.startDate).format('YYYY-MM-DD');
          }
          if (value.value === 'Other') {
            response = currentQuestion.enumResponses?.find(
              ({ response }) => response === value.value,
            );
            if (value?.startDate)
              extraDates.date1 = moment(value.startDate).format('YYYY-MM-DD');
            if (value?.endDate)
              extraDates.date2 = moment(value.endDate).format('YYYY-MM-DD');
          }
          if (
            errors.length !== 0 &&
            value.value === 'Other' &&
            response.response === 'Other' &&
            value.startDate &&
            !value.endDate
          ) {
            setError([{ text: 'Please provide the end date as well.' }]);
            return;
          }
          if (!response) {
            response = currentQuestion.enumResponses?.find(
              ({ response }) => response === value?.value,
            );
          } else {
            response = {
              ...response,
              response: `${value.value}: ${response.response}`,
            };
          }
          break;

        case 'ADDRESS':
          if (getTotalPercentage(value) !== 100) {
            setError([
              ...errors,
              { text: 'Please answer this question to continue' },
            ]);
            return;
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: value,
          };
          break;

        case 'MULTIADDRESS': {
          if (value?.length < 2) {
            setError([
              ...errors,
              { text: 'You must type at least 2 locations' },
            ]);
            return;
          }
          if (
            value?.some(
              ({ address, latitude, longitude }) =>
                !address || !latitude || !longitude,
            )
          ) {
            setError([...errors, { text: 'All rows must have addresses' }]);
            return;
          }
          if (value?.some(({ percent }) => !parseFloat(percent))) {
            setError([
              ...errors,
              {
                text:
                  'Please enter the percentage of the total beneficiaries of your program that are served in this location',
              },
            ]);
            return;
          }
          const sum = value?.reduce((all, { percent = 0 }) => all + percent, 0);
          if (sum !== 100) {
            setError([
              ...errors,
              {
                text:
                  'Whoops! These numbers don’t add up to 100%. If you have overlapping populations, you should read this article on ' +
                    'how to fix that',
              },
            ]);
            return;
          }
          response = {
            questionId: currentQuestion.dbId,
            dbId: null,
            response: value,
          };
          break;
        }
        default:
          response = currentQuestion?.enumResponses?.find(
            ({ response }) => response === value?.option,
          );
      }
      if (currentQuestion?.dbId === 40015 && !response && !value) {
        value = currentQuestion?.enumResponses?.[0]?.response;
      }
      if (
        questionId === 50001 ||
        currentQuestion?.dbId === 50001 ||
        questionId === 50002 ||
        currentQuestion?.dbId === 50002
      ) {
        value = value || forcedValues;
      }
      if (!response && value && (currentQuestion || questionId)) {
        response = {
          questionId: currentQuestion?.dbId || questionId,
          dbId: null,
          response: value,
        };
      }
      /* Handle multi reponses, for example multiple programs, this is an new commmon operation on allocation manager */
      if (
        qType === 'PROGRAM' ||
        qType === 'PROGRAM_CORRECT_TYPE' ||
        qType === 'PROGRAM_YEAR' ||
        qType === 'PROGRAM_MULTI'
      ) {
        setQuestionHistory(old => [...old, currentQuestion]);
        handleMultipleAnswers(value);
        return;
      }
      /* This is the current answers id, must be provied to edit and avoid saving a new one */
      const userAnswerId =
        answers.filter(ans => ans.questionId === response?.questionId)?.[0]
          ?.dbId ?? undefined;

      /* INTRO */
      if (currentQuestion?.questionType === 'INTRO') {
        saveOrUpdateAnswer({
          id: userAnswerId,
          reportId,
          ignoreClearData: currentQuestion?.dbId === 40000,
          questionId: currentQuestion?.dbId || currentQuestion?.id,
          text: '',
        });
        return;
      }
      if (
        (!value && optionalQuestion) ||
        (!value && currentQuestion?.dbId === 50007) ||
        (!value && currentQuestion?.dbId === 50028)
      ) {
        response = {
          response: '',
          questionId:
            currentQuestion?.dbId || currentQuestion?.id || questionId,
          dbId: currentQuestion?.enumResponses?.[0]?.dbId,
        };
      }
      if (!response) {
        setError('Please choose an option to continue.');
        return;
      }
      const form = {
        ...extraDates,
        id: userAnswerId || null,
        questionId: response.questionId,
        answerId: response.dbId,
        text: forcedValues || response.response,
        questionText:
          currentQuestion && currentQuestion.text
            ? currentQuestion.text
                .replace(/<Donation_total>/g, donationTotal)
                .replace(/<Program_name>/g, programName)
                .replace(/<Sponsor_name>/g, sponsorName)
            : section,
      };

      if (reportId) {
        form.reportId = reportId;
      }
      if (investmentId) {
        form.investmentId = investmentId;
      }
      /* if custom text is writen in any cuestion, replaces text on form */
      if (value?.custom) {
        form.text = value.custom;
      }

      /* if the answer is a PERIOD update date1, date2s */
      if (response.responseType === 'period') {
        if (!value.endDate || !value.startDate) {
          setError('Please complete the dates to continue.');
          return;
        }
        form.date1 = moment(value.startDate).format('YYYY-MM-DD');
        form.date2 = moment(value.endDate).format('YYYY-MM-DD');
        form.text = `${response.response} (${form.date1} to ${form.date2})`;
      }
      if (response.responseType === 'date') {
        if (!value.startDate) {
          setError([...errors, { text: 'Please complete the date.' }]);
          return;
        }
        form.date1 = moment(value.startDate).format('YYYY-MM-DD');
        form.text = `${response.response} (${form.date1})`;
        if (form.date2) {
          form.text = `${form.text} to (${form.date2})`;
        }
      }
      /* if the question is a DATE must update date1 */
      if (currentQuestion?.questionType === 'DATE') {
        form.date1 = moment(value.date).format('YYYY-MM-DD');
      }
      /* if the question is a PERIOD update date1, date2 */
      if (currentQuestion?.questionType === 'PERIOD') {
        form.date1 = moment(value.startDate).format('YYYY-MM-DD');
        form.date2 = moment(value.endDate).format('YYYY-MM-DD');
      }
      /* handle custom options */
      if (currentQuestion?.questionType === 'CUSTOM_OPTIONS' && value) {
        form.answerId = value?.answerId;
        form.text = value?.text ?? value?.type ?? value;
        if (!form.text && value?.type === 'link') {
          form.text = value?.type;
        }
        if (currentQuestion?.enumResponses?.[0]?.responseType === 'number') {
          if (!form.text || form.text === '0' || isNaN(form.text)) {
            setError([
              ...errors,
              { text: 'Please provide a value to continue' },
            ]);
            return;
          }
          if (
            currentQuestion?.dbId === 50003 ||
            currentQuestion?.dbId === 50004 ||
            currentQuestion?.dbId === 50005 ||
            currentQuestion?.dbId === 50006
          ) {
            const num = parseFloat(form.text);
            const hasDecimal = num % 1 !== 0;
            if (num < 1) {
              setError([
                ...errors,
                { text: 'Your program must reach at least one beneficiary' },
              ]);
              return;
            }
            if (hasDecimal) {
              setError([
                ...errors,
                {
                  text: 'Please specify a valid round number of beneficiaries',
                },
              ]);
              return;
            }
          }
          if (currentQuestion?.dbId === 50004) {
            const q3Value = answers.filter(ans => ans.questionId === 50003)?.[0]
              ?.text;
            if (q3Value && parseFloat(form.text) > parseInt(q3Value)) {
              setError([
                ...errors,
                {
                  text:
                    'Forecast value should not be higher than your set beneficiaries',
                },
              ]);
              return;
            }
          }
          if (currentQuestion?.dbId === 50005) {
            const q4Value = answers.filter(ans => ans.questionId === 50004)?.[0]
              ?.text;
            if (q4Value && parseFloat(form.text) > parseInt(q4Value)) {
              setError([
                ...errors,
                {
                  text:
                    'Forecast value should not be higher than your learn value',
                },
              ]);
              return;
            }
          }
          if (currentQuestion?.dbId === 50006) {
            const q5Value = answers.filter(ans => ans.questionId === 50005)?.[0]
              ?.text;
            if (q5Value && parseFloat(form.text) > parseInt(q5Value)) {
              setError([
                ...errors,
                {
                  text: `Your program cannot have more people act than learn to act. Please select an amount smaller than ${q5Value}`,
                },
              ]);
              return;
            }
          }
        }
        if (errors && errors.length > 0) {
          setError([...errors]);
          return;
        }
      }
      if (
        qType === 'BENEFICIARIES' &&
        (!value || value === 'Other' || isEmptyOrSpaces(value))
      ) {
        setError([...errors, { text: 'You must enter a value to continue' }]);
        return;
      }
      /* clear any error present */

      /* check if current question has and aditional resource_id */
      const resourceId = extraResourcesId?.[currentQuestion?.dbId];
      if (resourceId) {
        form.resourceId = resourceId;
      }

      const crtValue = form.text;
      const prevValue = (answers || [])?.filter(
        ans => ans.questionId === currentQuestion?.dbId,
      )?.[0]?.text;
      const isSameValueAsStored = crtValue === prevValue;
      if (
        isSameValueAsStored ||
        ignoreClearDataQuestionIds.includes(currentQuestion?.dbId)
      ) {
        form.ignoreClearData = true;
      }

      if (currentQuestion?.dbId === DATES_QUESTION_ID_1) {
        if (typeof value === 'string') {
          const period = parsePeriodValuesString(value);
          form.date1 = formatDateForGraphQL(period.startDate);
          form.date2 = formatDateForGraphQL(period.endDate);
          form.text = value;
        } else {
          form.date1 = value.startDate;
          form.date2 = value.endDate;
          form.text = `${
            value.option
              ? value.option.charAt(0).toUpperCase() + value.option.slice(1)
              : ''
          }: ${formattedDate(value.startDate)} - ${formattedDate(
            value.endDate,
          )}`;
        }
      }
      setQuestionHistory(old => [...old, currentQuestion]);
      setError(undefined);
      saveOrUpdateAnswer(form);
      if (form.questionId === DATES_QUESTION_ID_1 && form.date1 && form.date2) {
        updateReportStateAndEndDates(client, reportId, form.date1, form.date2);
      }
    },
    [
      questions,
      values,
      currentQuestion,
      validate,
      answers,
      donationTotal,
      programName,
      sponsorName,
      reportId,
      investmentId,
      extraResourcesId,
      setError,
      saveOrUpdateAnswer,
      onSectionEnd,
      handleMultipleAnswers,
      optionalQuestion,
      report.sponsorName,
    ],
  );

  const handleOrgWarningClose = useCallback(() => {
    setOrgWarningOpen(false);
    setPendingNextArgs(null);
    if (orgWarningResolveRef.current) {
      const resolve = orgWarningResolveRef.current;
      orgWarningResolveRef.current = null;
      resolve(false);
    }
  }, []);

  const handleOrgWarningConfirm = useCallback(
    async level1Note => {
      const args = pendingNextArgs;
      const resolve = orgWarningResolveRef.current;
      orgWarningResolveRef.current = null;
      setOrgWarningOpen(false);
      setPendingNextArgs(null);
      hasLevel1DonorNameRef.current = true;
      if (reportId) {
        try {
          await updateReportLevel1DonorName(
            client,
            reportId,
            level1Note || DONOR_NAME_FLAG_VALUE,
          );
          if (reloadReport) reloadReport();
        } catch (e) {
          // swallow; user can retry on next press
        }
      }
      if (resolve) {
        // Note-question flow: let SectionNoteUpload's own save proceed.
        resolve(true);
        return;
      }
      if (args) {
        handleNext(args.pressedButton, args.forcedValues);
      } else {
        handleNext();
      }
    },
    [client, reportId, pendingNextArgs, handleNext, reloadReport],
  );

  const handleNoteBeforeNext = useCallback(
    async description => {
      if (
        levelOneFeedbackEnabled &&
        !hasLevel1DonorNameRef.current &&
        description &&
        report?.sponsorName &&
        hasDonorName(description, report.sponsorName)
      ) {
        setPendingNextArgs({ value: description });
        setOrgWarningOpen(true);
        return new Promise(resolve => {
          orgWarningResolveRef.current = resolve;
        });
      }
      return true;
    },
    [levelOneFeedbackEnabled, report?.sponsorName],
  );

  /*
    Handles when Back button is pressed in section
  */
  const handleBack = useCallback(() => {
    // TODO: commented line to preserve set values on BACK, this is experimental feature must be heavily tested to detect any side effects.
    //setValues(old => omit(old, currentQuestion.dbId))
    setError();
    if (sectionsFirstQuestionIds.includes(currentQuestion.dbId)) {
      onRequestBack();
      return;
    }
    const sectionAnswers = answers.filter(
      ans => ans.question.section === SECTION_FILTER[section],
    );
    const currentIndex = findIndex(
      sectionAnswers,
      ans => ans.question.dbId === currentQuestion.dbId,
    );
    const prevAns =
      sectionAnswers && sectionAnswers.length > 0
        ? sectionAnswers.slice(-1)[0]
        : null;
    if (currentQuestion?.dbId === 50007 && sectionAnswers) {
      const prevToSummaryId =
        sectionAnswers[sectionAnswers.length - 1].question.dbId;
      const prevToSummary = questions?.find(q => q.dbId === prevToSummaryId);
      if (prevToSummary) {
        onQuestionChange(prevToSummary);
        setCurrentQuestion(prevToSummary);
        return;
      }
    }
    if (currentIndex > 0 || (currentIndex === -1 && sectionAnswers.length > 0)) {
      const previousBudgetId = checkBudgetBackNavigation(currentQuestion?.dbId);
      const prevQuestionId =
        previousBudgetId ||
        sectionAnswers[
          currentIndex === -1 ? sectionAnswers.length - 1 : currentIndex - 1
        ].question.dbId;
      const prevQuestion = questions?.find(q => q.dbId === prevQuestionId);
      onQuestionChange(prevQuestion);
      setCurrentQuestion(prevQuestion);
    } else if (section === 'allocation') {
      if (questionHistory.length) {
        const last = questionHistory[questionHistory.length - 1];
        const updatedHistory = cloneDeep(questionHistory.slice(0, -1));
        setCurrentQuestion(last);
        setQuestionHistory(updatedHistory);
        onQuestionChange(last);
      }
    } else if (currentQuestion?.questionType === 'INTRO') {
      onRequestBack();
    } else if (
      currentIndex === -1 &&
      prevAns &&
      prevAns?.question?.questionType === 'INTRO'
    ) {
      onRequestBack();
    } else {
      if (currentQuestion?.dbId === 110001) return onRequestBack();
      const last = questionHistory[questionHistory.length - 1];
      const updatedHistory = cloneDeep(questionHistory.slice(0, -1));
      if (last) {
        setCurrentQuestion(last);
        setQuestionHistory(updatedHistory);
        onQuestionChange(last);
      } else {
        const sectionHasAnswers = sectionAnswers && sectionAnswers.length > 0;
        if (sectionHasAnswers) {
          const prevDbQuestionId =
            sectionAnswers[sectionAnswers.length - 1].questionId;
          const prev = questions?.find(q => q.dbId === prevDbQuestionId);
          if (prev.dbId !== currentQuestion.dbId) {
            onQuestionChange(prev);
            setCurrentQuestion(prev);
          } else {
            onRequestBack();
          }
        } else {
          onRequestBack();
        }
      }
    }
  }, [
    setError,
    answers,
    currentQuestion,
    section,
    questions,
    onQuestionChange,
    questionHistory,
    onRequestBack,
  ]);

  const getValue = () => {
    let newVal = values[currentQuestion?.dbId];
    if (currentQuestion?.feExtraRequirement === 'require_all_choices') {
      if (typeof newVal === 'string') {
        try {
          newVal = {
            type: currentQuestion?.enumResponses?.[0]?.responseType,
            answerId: currentQuestion?.enumResponses?.[0]?.dbId,
            text: JSON5.parse(newVal.replaceAll('\'', '"')),
          };
        } catch (err) { /* empty */ }
      }
      return newVal;
    }
    return newVal;
  };

  const replaceOtpTag = array => {
    const beneficiary =
      answers?.filter(ans => ans.questionId === 40004)?.[0]?.text ||
      'Beneficiaries';
    const servCollKey = answers?.filter(ans => ans.questionId === 40002)?.[0]
      ?.text;
    const servKey = answers?.filter(ans => ans.questionId === 40003)?.[0]?.text;
    const goal = (
      (serviceCollections || []).filter(coll => coll.name === servCollKey)?.[0]
        ?.services ?? []
    ).filter(serv => serv.name === servKey)?.[0]?.goal;
    const serviceGoal = goal || 'match your service goal';
    const newArray = array.map(item => {
      return item
        .replace(/<Primary_beneficiary>/g, beneficiary)
        .replace(/<Primary_beneficiary_or_Intermediary>/g, beneficiary)
        .replace(/<Service_goal>/g, serviceGoal);
    });
    return newArray;
  };

  const isLoading = loadingNextQuestion || loadingNew || loadingUpdate;

  const content = useMemo(() => {
    const isReportPassed =
      report.stage === 'FINAL' ||
      report.stage === 'INTERIM' ||
      isReportDatesPassed(values, report.year, report.stage, report.endDate);

    const hasChosenAnimal = () => {
      return answers.some(
        answer =>
          answer.questionId === 40006 &&
          answer.text.toLowerCase().includes('animal') &&
          answer.text.toLowerCase().includes('themselves'),
      );
    };

    // Navigation for the optional note steps appended to each section (TIPD-3323)
    const getNoteNavigation = noteId => {
      switch (noteId) {
        case NOTE_QUESTION_IDS.activities:
          return {
            next: () =>
              handlePressOption(0, getSection('impact_summary').index),
            prev: handleBack,
          };
        case NOTE_QUESTION_IDS.impactDetails:
          return {
            next: () => handlePressOption(0, getSection('beneficiaries').index),
            prev: () =>
              handlePressOption(0, getSection('indicator_details').index),
          };
        case NOTE_QUESTION_IDS.beneficiaries:
          return {
            next: () => handlePressOption(0, getSection('budget').index),
            prev: () => handlePressOption(0, getSection('beneficiaries').index),
          };
        default:
          return { next: onRequestBack, prev: onRequestBack };
      }
    };

    // Opens the section's note question if it exists, otherwise runs the fallback
    const goToNoteOrSection = (noteId, fallbackNav) => {
      const noteQuestion =
        questions.find(q => q.dbId === noteId) ||
        baseQuestions?.find(q => q.dbId === noteId);
      if (noteQuestion) {
        onQuestionChange(noteQuestion);
        setCurrentQuestion(noteQuestion);
      } else {
        fallbackNav();
      }
    };

    /* TEMP: displays location section */
    if (loadingNextQuestion) {
      return (
        <SectionBase title={section}>
          <Fragment>
            <Skeleton />
            <Skeleton width="80%" />
            <Skeleton width="80%" />
          </Fragment>
        </SectionBase>
      );
    }

    if (
      currentQuestion &&
      NOTE_QUESTION_ID_LIST.includes(currentQuestion.dbId)
    ) {
      const rawText = currentQuestion.text || '';
      const [rawTitle, rawBody] = rawText.includes('[HELP]')
        ? rawText.split('[HELP]')
        : [rawText, currentQuestion.helpContent];
      const withProgram = t =>
        (t || '')
          .replace(/<Program_name>/g, programName || '')
          .replace(/\{program name\}/gi, programName || '');
      const noteNav = getNoteNavigation(currentQuestion.dbId);
      return (
        <SectionNoteUpload
          reportId={reportId}
          questionId={currentQuestion.dbId}
          title={withProgram(rawTitle)}
          body={withProgram(rawBody)}
          hasFileUpload={
            String(currentQuestion.questionType).toUpperCase() === 'FILE'
          }
          guidanceText={guidanceText}
          initialValue={values[currentQuestion.dbId]}
          beforeNext={handleNoteBeforeNext}
          onNext={() => {
            reloadAnswers && reloadAnswers();
            noteNav.next();
          }}
          onPrevious={noteNav.prev}
        />
      );
    }

    if (section === 'Impact Model Details') {
      if (!completedImpactSummary) {
        return (
          <SectionBase title={section}>
            <p>
              <strong>Impact Model Details</strong> are only available once the{' '}
              <strong>Impact Model Builder</strong> has been completed.
              <br />
              Please complete your <strong>Impact Model Builder</strong> by{' '}
              <Link onClick={handleOpenImpactSummarySection}>
                clicking here
              </Link>
            </p>
          </SectionBase>
        );
      }
      const learnEnabled = hasChosenAnimal() ? false : values[40007] === 'Yes';
      const actEnabled = hasChosenAnimal() ? false : values[40008] === 'Yes';

      const capacityDevelopmentIndicators = values[40021];
      const capacityDevelopmentEnabled = isCapacityDevelopmentEnabled(
        capacityDevelopmentIndicators,
      );
      const handleNextSection = () =>
        goToNoteOrSection(NOTE_QUESTION_IDS.impactDetails, () =>
          handlePressOption(0, getSection('beneficiaries').index),
        );
      const handlePreviousSection = () =>
        handlePressOption(0, getSection('impact_summary').index);
      if (values[50001] || currentQuestion?.dbId === 50002) {
        return (
          <ImpactFullModel
            answers={values}
            report={report}
            reportId={reportId}
            programName={programName}
            isReportPassed={isReportPassed}
            capacityDevelopmentEnabled={capacityDevelopmentEnabled}
            learnEnabled={learnEnabled}
            actEnabled={actEnabled}
            handleNext={handleNextSection}
            handleBack={handlePreviousSection}
            reloadAnswers={reloadAnswers}
            guidanceText={guidanceText}
          />
          // <IndicatorSummary
          //   reportId={reportId}
          //   programName={programName}
          //   isReportPassed={isReportPassed}
          //   capacityDevelopmentEnabled={capacityDevelopmentEnabled}
          //   learnEnabled={learnEnabled}
          //   actEnabled={actEnabled}
          //   handleNext={handleNext}
          //   handleBack={handleBack}
          //   reloadAnswers={reloadAnswers}
          //   onNextSection={handleNextSection}
          //   handleValueChange={handleValueChange}
          //   guidanceText={guidanceText}
          // />
        );
      }
      return (
        <SectionIndicatorDetails
          reportId={reportId}
          allIndicators={mapIndicatorsById(indicators)}
          beneficiaryTerm={values[40004]}
          reportDates={values[DATES_QUESTION_ID_1]}
          intermediaries={values[40022]}
          serviceGoal={values[40003]}
          serviceType={values[40006]}
          value={values[currentQuestion?.dbId]}
          capacityDevelopmentEnabled={capacityDevelopmentEnabled}
          learnEnabled={learnEnabled}
          actEnabled={actEnabled}
          isReportPassed={isReportPassed}
          programName={programName}
          reloadAnswers={reloadAnswers}
          onPreviousSection={handleOpenImpactSummarySection}
          handleNext={handleNext}
          goToDateSection={() =>
            handlePressOption(0, getSection('dates').index)
          }
          onNextSection={handleNextSection}
          guidanceText={guidanceText}
        />
      );
    }

    const navSectionId = isInterimStage ? 1 : 2;

    if (section === 'Narrative' && currentQuestion?.dbId) {
      return (
        <Narrative
          reportId={reportId}
          isThirdParty={isThirdParty}
          programName={programName}
          question={currentQuestion}
          value={values[currentQuestion?.dbId]}
          handleNext={handleNext}
          handleBack={handleBack}
          handleValueChange={handleValueChange}
          onPreviousSection={
            isFinalizeStage
              ? () => handlePressOption(2, 2)
              : () => handlePressOption(0, getSection('beneficiaries').index)
          }
          onNextSection={() => handlePressOption(0, getSection('budget').index)}
          guidanceText={guidanceText}
        />
      );
    }
    if (section === 'Review & Submit') {
      return (
        <Review
          report={report}
          reportId={reportId}
          isReportPassed={isReportPassed}
          isInterimStage={isInterimStage}
          isFinalizeStage={isFinalizeStage}
          feedbackType={feedbackType}
          programName={programName}
          answers={answers}
          reloadReport={reloadReport}
          reloadAnswers={reloadAnswers}
          onPreviousSection={() =>
            handlePressOption(0, getSection('budget').index)
          }
          handlePress={handlePressOption}
          customQuestions={customQuestions}
        />
      );
    }
    if ((isFinalizeStage || isInterimStage) && section === 'Budget') {
      return (
        <FinalizeBudget
          report={report}
          answers={answers}
          isInterimStage={isInterimStage}
          isFinalizeStage={isFinalizeStage}
          questions={baseQuestions}
          reloadAnswers={reloadAnswers}
          onPreviousSection={() => handlePressOption(navSectionId, 3)}
          onNextSection={() => handlePressOption(navSectionId, 5)}
        />
      );
    }
    if (section === 'Budget' && currentQuestion?.dbId) {
      return (
        <Budget
          reportId={reportId}
          programName={programName}
          question={currentQuestion}
          value={values[currentQuestion?.dbId]}
          allValues={values}
          isReportPassed={isReportPassed}
          handleNext={handleNext}
          handleBack={handleBack}
          feedbackByQuestionId={{}}
          handleValueChange={handleValueChange}
          onPreviousSection={() =>
            handlePressOption(
              0,
              getSection(
                isReportPassed ? 'narrative' : 'beneficiaries',
                isReportPassed ? FINAL_REPORT_SECTIONS() : undefined,
              ).index,
            )
          }
          onNextSection={() =>
            handlePressOption(0, getSection('finalize_report').index)
          }
          guidanceText={guidanceText}
        />
      );
    }

    if (section === 'Final Results' || section === 'Interim Forecasts') {
      return (
        <ImpactFullModel
          report={report}
          reportId={reportId}
          programName={programName}
          answers={values}
          isReportPassed={isReportPassed}
          guidanceText={guidanceText}
          reloadAnswers={reloadAnswers}
          handleBack={() => handlePressOption(navSectionId, 0)}
          handleNext={() => handlePressOption(navSectionId, 2)}
        />
        // <FinalResults
        //   report={report}
        //   answers={answers}
        //   programName={programName}
        //   questions={baseQuestions}
        //   reloadAnswers={reloadAnswers}
        //   loadingAnswers={loadingAnswers}
        //   onPreviousSection={() => handlePressOption(2, 0)}
        //   onNextSection={() => handlePressOption(2, 2)}
        // />
      );
    }

    if (section === 'Program Overview') {
      return (
        <ReportFinalizeSummary
          report={report}
          answers={answers}
          isInterimStage={isInterimStage}
          programName={programName}
          questions={baseQuestions}
          loadingAnswers={loadingAnswers}
          reloadAnswers={reloadAnswers}
          handlePressOption={handlePressOption}
        />
      );
    }

    if (section === 'Progress Notes') {
      return (
        <ProgressNotes
          report={report}
          answers={answers}
          isInterimStage={isInterimStage}
          programName={programName}
          questions={baseQuestions}
          loadingAnswers={loadingAnswers}
          reloadAnswers={reloadAnswers}
          beforeNext={handleNoteBeforeNext}
          onNextSection={() => handlePressOption(navSectionId, 4)}
          onPreviousSection={() => handlePressOption(navSectionId, 2)}
        />
      );
    }

    if (sectionId === 3 && section === 'Beneficiaries') {
      const beneficiary =
        answers?.filter(ans => ans.questionId === 40004)?.[0]?.text ||
        'Beneficiaries';
      return (
        <FinalizeBeneficiaries
          reportId={reportId}
          report={report}
          answers={answers}
          questions={baseQuestions}
          question={currentQuestion}
          onPreviousSection={onRequestBack}
          reloadBeneficiaries={beneficiariesRefetch}
          reloadAnswers={reloadAnswers}
          loadingAnswers={loadingAnswers}
          onNextSection={() => handlePressOption(navSectionId, 3)}
          programName={programName}
          beneficiary={beneficiary}
          beneficiaries={beneficiaries}
          beneficiariesLoading={beneficiariesLoading}
          beneficiariesError={beneficiariesError}
          beneficiariesRefetch={beneficiariesRefetch}
        />
      );
    }

    if (section === 'Activities') {
      return (
        <Activities
          reportId={reportId}
          question={currentQuestion}
          onPreviousSection={onRequestBack}
          programName={programName}
          allValues={values}
          questions={baseQuestions}
          savedAnswers={answers}
          isReportPassed={isReportPassed}
          reloadAnswers={reloadAnswers}
          beneficiaryTerm={values[40004]}
          intermediaries={values[40022]}
          handleValueChange={handleValueChange}
          guidanceText={guidanceText}
          isProgramLevelQuestion={isProgramLevelQuestion}
        />
      );
    }

    if (section === 'Beneficiaries') {
      const value = values[currentQuestion?.dbId];
      const beneficiary =
        answers?.filter(ans => ans.questionId === 40004)?.[0]?.text ||
        'Beneficiaries';
      return (
        <BeneficiaryLanding
          value={value}
          reportId={reportId}
          question={currentQuestion}
          allAnswers={values || {}}
          onPreviousSection={onRequestBack}
          reloadAnswers={reloadAnswers}
          onNextSection={() =>
            goToNoteOrSection(NOTE_QUESTION_IDS.beneficiaries, () =>
              handlePressOption(0, getSection('budget').index),
            )
          }
          programName={programName}
          beneficiary={beneficiary}
          beneficiaries={beneficiaries}
          beneficiariesLoading={beneficiariesLoading}
          beneficiariesError={beneficiariesError}
          beneficiariesRefetch={beneficiariesRefetch}
          guidanceText={guidanceText}
        />
      );
    }

    if (currentQuestion) {
      let { questionType } = currentQuestion; // TODO: remove this once DB is updated
      let extraData = {};
      const {
        text,
        helpContent,
        enumResponses,
        enumPrograms,
      } = currentQuestion;

      const helpRaw = (text || '').includes('[HELP]')
        ? text.split('[HELP]')?.[1]
        : helpContent;
      const helpText = /<.*>/.test(helpRaw)
        ? replaceOtpTag([helpRaw])[0]
        : helpRaw;
      const primaryBeneficiary =
        answers?.filter(ans => ans.questionId === 40004)?.[0]?.text ||
        'Beneficiaries';

      const questionText = (
        <QuestionText
          {...{
            text,
            donationTotal,
            programName,
            sponsorName,
            reportStartDate,
            reportEndDate,
            primaryBeneficiary,
            reportYear: report.year,
            intermediaries: values[40022],
          }}
          dbId={currentQuestion?.dbId}
          guidanceText={guidanceText}
        />
      );
      // INFO: had to comment this as is producing an strange issue, selecting options does not work.
      // However, this is notrquired as BE V2 will return all text with value replaced
      /*const options = enumResponses
        ?.map(({ response }) => response)
        ?.map(text => <QuestionText {...{ text, donationTotal, programName, sponsorName, reportStartDate, reportEndDate }}/>)*/
      let options = enumResponses?.map(({ response }) => response);
      if (
        Array.isArray(enumPrograms) &&
        [
          'PROGRAM',
          'PROGRAM_CORRECT_TYPE',
          'PROGRAM_YEAR',
          'PROGRAM_YEAR_AMOUNT',
        ].indexOf(questionType) >= 0
      ) {
        options =
          Array.isArray(enumPrograms) &&
          enumPrograms
            .map(prg => {
              const reports =
                questionType === 'PROGRAM_YEAR'
                  ? prg?.reports.filter(rpt => rpt?.year)
                  : [];
              reports.forEach(rpt => {
                rpt.period = `${moment(rpt.start_date, 'YYYY-MM-DD').format(
                  'MMMM DD, YYYY',
                )} - ${moment(rpt.end_date, 'YYYY-MM-DD').format(
                  'MMMM DD, YYYY',
                )}`;
              });
              const sd = prg?.reports?.[0]?.start_date;
              const ed = prg?.reports?.[0]?.end_date;
              const period =
                sd && ed
                  ? `${moment(sd, 'YYYY-MM-DD').format(
                      'MMM DD, YYYY',
                    )} - ${moment(ed, 'YYYY-MM-DD').format('MMM DD, YYYY')}`
                  : 'Period not set...';
              let optYear = prg?.reports?.[0]?.year || '';
              // for q7 adds custom year to subtitle.
              // no year detected, is auto or is custom.
              // should pick the correct one auto/custom.
              // to detect if auto, should find if q5 exist and prg.id values is 'Yes'
              if (
                optYear === '' &&
                values?.[20005] &&
                (values?.[20005] || []).filter(
                  row => parseInt(row?.id) === prg?.id,
                )?.[0]?.value === 'Yes'
              ) {
                optYear =
                  programs.filter(p => p.id === prg.id)?.[0]?.reports?.[0]
                    ?.year || '';
              }
              if (
                values &&
                values?.[20006] &&
                (values?.[20006] || []).filter(
                  row => parseInt(row.id) === prg.id,
                ).length > 0
              ) {
                const val = values?.[20006].filter(
                  row => parseInt(row.id) === prg.id,
                )?.[0]?.value;
                if (val.length === 4) {
                  optYear = val;
                }
              }
              const valid = Boolean(prg.name);
              return {
                id: prg.id,
                name: prg.name,
                period,
                year: optYear,
                reports,
                valid,
              };
            })
            .filter(program => program.valid);
      }
      if (questionType === 'PROGRAM_MULTI') {
        if (Array.isArray(enumPrograms)) {
          options = enumPrograms?.reduce((acc, prg) => {
            const reports = prg?.reports.filter(rpt => rpt?.year) ?? [];
            if (reports.length > 0) {
              const manualQ6Year = (values?.[20006] ?? []).filter(
                val => parseInt(val.id) === prg.id,
              )?.[0]?.value;
              const period = manualQ6Year
                ? manualQ6Year
                : `${moment(reports[0].start_date, 'YYYY-MM-DD').format(
                    'MMM DD, YYYY',
                  )} - ${moment(reports[0].end_date, 'YYYY-MM-DD').format(
                    'MMM DD, YYYY',
                  )}`;
              acc.push({
                id: prg.id,
                name: prg.name,
                year: manualQ6Year ? manualQ6Year : reports[0].year,
                report: manualQ6Year ? undefined : reports[0].id,
                period,
              });
            }
            return acc;
          }, []);
        }
      }

      // TODO: remove when DB is updated
      if (currentQuestion?.dbId === 20014) {
        const year = new Date().getFullYear();
        options = [
          {
            name: accountName
              ? `${accountName} General Operations`
              : 'General Operations',
            reports: [
              {
                name: 'general',
                report_id: 0,
                year,
                start_date: `${year}-01-01`,
                end_date: `${year}-12-31`,
                period: `This fiscal year (01 January ${year} - 31 December ${year})`,
                fully_allocated: false,
              },
            ],
          },
        ];
      }
      if (currentQuestion?.dbId === 20016) {
        questionType = 'PROGRAM_CORRECT_TYPE';
      }
      let services = [];
      let collection = '';
      let allIndicators = null;
      if (questionType === 'SERVICE') {
        collection = values?.[currentQuestion?.dbId - 1];
        services =
          serviceCollections.find(ser => ser.name.trim() === collection.trim())
            ?.services || [];
      }
      if (questionType === 'BENEFICIARIES') {
        if (currentQuestion?.dbId === 40022) {
          options = options.map((name, id) => ({ id, name }));
        } else {
          options = beneficiaryTags;
        }
      }
      if (currentQuestion?.dbId === 40010) {
        const selectedService = serviceCollections
          .find(coll => coll.name.trim() === values?.[40002]?.trim())
          ?.services.find(ser => ser.name.trim() === values?.[40003]?.trim());
        const socialImpactIndicators = getImpactIndicators(
          indicators,
          selectedService?.name,
        );
        options = socialImpactIndicators.options;
        const baseServices = (indicators ?? []).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        allIndicators = baseServices || socialImpactIndicators.restIndicators;
      }
      // Is Impact Summary Section Question Summary Table
      if (currentQuestion?.dbId === 40015) {
        const summaryCollection = serviceCollections?.filter(
          coll => coll.name === values?.[40002],
        )?.[0];
        const allServices = summaryCollection?.services ?? [];
        const summaryService = allServices?.filter(
          ser =>
            ser.name?.replace(/\u200b/g, '') === values?.[40003] &&
            ser.version === 'v4',
        )?.[0];

        const learnEnabled = hasChosenAnimal()
          ? false
          : answers?.find(ans => ans.questionId === 40007)?.text === 'Yes';

        const actEnabled = hasChosenAnimal()
          ? false
          : answers?.find(ans => ans.questionId === 40008)?.text === 'Yes';

        const detectedService = serviceCollections
          .map(col =>
            col.services.find(
              ser =>
                ser.name?.replace(/\u200b/g, '') === values?.[40003] &&
                ser.version === 'v4',
            ),
          )
          .filter(Boolean)?.[0];

        extraData = {
          service: summaryService || detectedService,
          services: allServices,
          reportId,
          indicators,
          learnEnabled,
          actEnabled,
        };
      }
      // forecast summary or forecast details
      if (currentQuestion?.dbId === 50007 || currentQuestion?.dbId === 50028) {
        const beneficiary =
          answers?.filter(ans => ans.questionId === 40004)?.[0]?.text ??
          'Beneficiary';
        extraData = {
          reportId,
          beneficiary,
        };
      }
      switch (questionType) {
        case 'RADIO': {
          if (currentQuestion?.dbId === 20010) {
            return (
              <SectionYesNoDollars
                title={!investmentId && startCase(section)}
                subTitle={questionText}
                helpText={helpText}
                options={options}
                value={values[currentQuestion?.dbId]}
                total={donationTotal}
                guidanceText={guidanceText}
                onChange={handleValueChange}
                questionId={currentQuestion?.dbId}
                error={error}
                programName={
                  currentQuestion?.sectionStart &&
                  currentQuestion?.enumPrograms?.[0]?.name
                }
                extraClass={`Q_${currentQuestion?.dbId}`}
              />
            );
          }

          const sectAnswers = answers.filter(ans =>
            SECTION_FILTER[section] === 'impact_summary'
              ? ans.question.section === 'impact_summary' ||
                ans.question.section === 'indicator_details'
              : ans.question.section === SECTION_FILTER[section],
          );
          const userAnswerId =
            answers.filter(ans => ans.questionId === currentQuestion?.dbId)?.[0]
              ?.dbId || undefined;
          const prevQuestion =
            sectAnswers?.[sectAnswers.length - 1]?.questionId;
          const radioOptions = options.some(item => /<.*>/.test(item))
            ? replaceOtpTag(options)
            : options;
          const isImpactSummaryCompleted =
            answers.filter(({ questionId }) => questionId === 40015).length > 0;
          // const summaryCollection = serviceCollections?.filter(
          //   coll => coll.name === values?.[40002],
          // )?.[0];
          // extraData.services =
          //   extraData?.services || summaryCollection?.services || [];

          return (
            <SectionMultiRadio
              section={section}
              isDebug={isDebug}
              title={!investmentId && startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              options={radioOptions}
              isReportPassed={isReportPassed}
              reportDates={values[DATES_QUESTION_ID_1]}
              values={values}
              intermediaries={values[40022]}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              setReportBuilderValues={setValues}
              questionId={currentQuestion?.dbId}
              beneficiaryTerm={values[40004]}
              error={error}
              guidanceText={guidanceText}
              handleNext={handleNext}
              handleBack={handleBack}
              choiceIsSelected={choiceIsSelected}
              reloadAnswers={reloadAnswers}
              programName={
                programName ||
                (currentQuestion?.sectionStart &&
                  currentQuestion?.enumPrograms?.[0]?.name)
              }
              extraClass={`Q_${currentQuestion?.dbId}`}
              extraData={extraData}
              prevQuestion={prevQuestion}
              sectAnswers={sectAnswers}
              userAnswerId={userAnswerId}
              isImpactSummaryCompleted={isImpactSummaryCompleted}
            />
          );
        }
        case 'TREE': {
          let treeOptions = enumResponses.reduce((acc, item) => {
            const labelInTree = findIndex(
              acc,
              ac => ac.label === item.treeGroup,
            );
            if (labelInTree > -1) {
              acc[labelInTree].options.push(item);
            } else {
              acc.push({ label: item.treeGroup, options: [item] });
            }
            return acc;
          }, []);
          if (treeOptions.length === 1 && !treeOptions[0].label) {
            treeOptions = treeOptions[0].options.map(option => ({
              ...option,
              label: option.response,
            }));
          }
          const otherType =
            enumResponses?.[enumResponses.length - 1]?.responseType;
          if (otherType === 'period') {
            return (
              <SectionPeriod
                report={report}
                reportYear={report.year}
                reportId={reportId}
                programId={programId}
                title={investmentId ? '' : startCase(section)}
                subTitle={questionText}
                helpText={helpText}
                options={options}
                values={values}
                value={values[currentQuestion?.dbId]}
                onChange={handleValueChange}
                questionId={currentQuestion?.dbId}
                otherType={otherType}
                isLoading={isLoading}
                error={error}
                handleNext={handleNext}
                handleBack={handleBack}
                guidanceText={guidanceText}
              />
            );
          }
          return (
            <SectionRadioTree
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              options={treeOptions}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              total={donationTotal}
              guidanceText={guidanceText}
            />
          );
        }
        case 'MIXED':
          return (
            <SectionPeriod
              report={report}
              reportId={reportId}
              reportYear={report.year}
              programId={programId}
              title={investmentId ? '' : startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              options={options}
              values={values}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              otherType={
                enumResponses?.[enumResponses.length - 1]?.responseType
              }
              isLoading={isLoading}
              error={error}
              handleNext={handleNext}
              handleBack={handleBack}
              guidanceText={guidanceText}
            />
          );
        case 'PERIOD':
          return (
            <SectionPeriod
              report={report}
              reportId={reportId}
              reportYear={report.year}
              programId={programId}
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              isLoading={isLoading}
              values={values}
              options={options}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              handleNext={handleNext}
              handleBack={handleBack}
              guidanceText={guidanceText}
            />
          );
        case 'DATE':
          return (
            <SectionDate
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'LONG_TEXT':
          return (
            <SectionLongText
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
              isProgramLevelQuestion={isProgramLevelQuestion}
              programId={programId}
            />
          );
        case 'PROGRAM':
          return (
            <SectionPrograms
              subTitle={questionText}
              helpText={helpText}
              options={options}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'PROGRAM_CORRECT_TYPE':
          return (
            <SectionProgramsYesNo
              subTitle={questionText}
              helpText={helpText}
              options={options}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'PROGRAM_YEAR':
          return (
            <SectionProgramYear
              subTitle={questionText}
              helpText={helpText}
              accountName={accountName}
              options={options}
              allValues={values}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'PROGRAM_MULTI':
          return (
            <SectionProgramMulti
              subTitle={questionText}
              helpText={helpText}
              options={options}
              enums={enumResponses}
              programs={enumPrograms}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
              isProgramLevelQuestion={isProgramLevelQuestion}
            />
          );
        case 'PROGRAM_YEAR_AMOUNT':
          return (
            <SectionProgramYearAmount
              subTitle={questionText}
              helpText={helpText}
              options={options}
              total={donationTotal}
              enums={enumResponses}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              onSetError={setError}
              guidanceText={guidanceText}
            />
          );
        case 'INTRO':
          if (currentQuestion?.dbId === 40000) {
            return (
              <ImpactSummaryIntro
                guidanceText={guidanceText}
                handleNext={() => handleNext(true)}
              />
            );
          }
          return <LocationLanding />;
        case 'ADDRESS':
          return (
            <SectionLocation
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'MULTIADDRESS':
          return (
            <SectionMultiAddress
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              onSetError={setError}
              guidanceText={guidanceText}
            />
          );
        case 'SERVICE_COLLECTION':
          return (
            <SectionServiceCollection
              data={serviceCollections}
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              guidanceText={guidanceText}
              beneficiaryTerm={values[40004]}
              intermediaries={values[40022]}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              onSetError={setError}
              answers={answers}
              isProgramLevelQuestion={isProgramLevelQuestion}
            />
          );
        case 'SERVICE':
          return (
            <SectionService
              data={services}
              title={startCase(section)}
              subTitle={questionText}
              helpText={collection}
              beneficiaryTerm={values[40004]}
              intermediaries={values[40022]}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              onSetError={setError}
              answers={answers}
              guidanceText={guidanceText}
              isProgramLevelQuestion={isProgramLevelQuestion}
            />
          );
        case 'BENEFICIARIES':
          return (
            <SectionService
              data={options}
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              beneficiaryTerm={values[40004]}
              intermediaries={values[40022]}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              onSetError={setError}
              answers={answers}
              guidanceText={guidanceText}
              isProgramLevelQuestion={isProgramLevelQuestion}
            />
          );
        case 'CHECKBOXES':
          if (allIndicators) {
            return (
              <SectionIndicators
                title={startCase(section)}
                subTitle={questionText}
                helpText={helpText}
                options={options}
                programName={programName}
                beneficiaryTerm={values[40004]}
                value={values[currentQuestion?.dbId]}
                onChange={handleValueChange}
                questionId={currentQuestion?.dbId}
                allIndicators={allIndicators}
                error={error}
                guidanceText={guidanceText}
                isProgramLevelQuestion={isProgramLevelQuestion}
              />
            );
          }
          return (
            <SectionCheckboxes
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              options={options}
              indicators={indicators}
              causeCategory={values[40002]}
              serviceName={values[40003]}
              beneficiaryTerm={values[40004]}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              allIndicators={allIndicators}
              error={error}
              answers={answers}
              guidanceText={guidanceText}
            />
          );
        case 'NUMBER':
          return (
            <SectionNumber
              title={startCase(section)}
              subTitle={questionText}
              helpText={helpText}
              value={values[currentQuestion?.dbId]}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              guidanceText={guidanceText}
            />
          );
        case 'CUSTOM_OPTIONS':
          return (
            <SectionCustomOptions
              title={startCase(section)}
              options={currentQuestion.enumResponses}
              subTitle={questionText}
              helpText={helpText}
              value={getValue()}
              onChange={handleValueChange}
              questionId={currentQuestion?.dbId}
              error={error}
              handleBack={handleBack}
              reportId={reportId}
              extraData={extraData}
              guidanceText={guidanceText}
              feExtraRequirement={currentQuestion.feExtraRequirement}
            />
          );
        default:
          return (
            <>
              <div>Support for {questionType} missing </div>
              <div>
                Q{currentQuestion?.dbId}: {questionText}
              </div>
            </>
          );
      }
    } else {
      return (
        <SectionErrorBanner
          sectionName={section}
          questionId={currentQuestion?.dbId}
          guidanceText={guidanceText}
          className="error-alert"
        />
      );
    }
  }, [
    loadingNextQuestion,
    currentQuestion,
    section,
    donationTotal,
    programName,
    sponsorName,
    reportStartDate,
    reportEndDate,
    values,
    programs,
    beneficiaries,
    beneficiariesLoading,
    accountName,
    serviceCollections,
    beneficiaryTags,
    indicators,
    investmentId,
    handleValueChange,
    error,
  ]); // eslint-disable-line

  const handleDismissError = useCallback(() => {
    setError();
  }, [setError]);

  const backTitle = useMemo(
    () =>
      currentQuestion?.questionType === 'INTRO' ? 'Previous Section' : 'Back',
    [currentQuestion],
  );
  const nextTitle = useMemo(
    () =>
      currentQuestion?.questionType === 'INTRO'
        ? 'Begin Section'
        : 'Save & Continue',
    [currentQuestion],
  );

  if (section === INTRO_SECTION_TEXT) {
    return (
      <Intro
        guidanceText={guidanceText}
        onNextSection={() => handlePressOption(0, 1)}
      />
    );
  }

  if (keepCached && onSummary) {
    return null;
  }

  if (!showSectionController) return null;

  return (
    <DonorNameFlagProvider
      donorName={report?.sponsorName}
      answers={donorNameAnswers}
    >
      <OrgNameWarningModal
        open={orgWarningOpen}
        isMatchedDonorName={true}
        onClose={handleOrgWarningClose}
        onConfirm={handleOrgWarningConfirm}
        programName={pendingNextArgs?.value || ''}
      />
      <OverwriteProgramAnswerModal
        open={pendingProgramOverwrite != null}
        questionTitle={pendingProgramOverwrite?.questionTitle ?? null}
        programAnswerText={pendingProgramOverwrite?.programText ?? ''}
        reportAnswerText={pendingProgramOverwrite?.reportText ?? ''}
        savingState={savingProgramAnswer}
        onSkip={handleSkipProgramOverwrite}
        onOverwrite={handleConfirmProgramOverwrite}
      />
      <Progress display={isLoading} />
      {Array.isArray(error) &&
        error.map(({ text }) => (
          <Alert
            key={text} //eslint-disable-line
            severity="error"
            action={<Button onClick={handleDismissError}>Dismiss</Button>}
          >
            {text}
          </Alert>
        ))}
      {showQuestionDetail && (
        <h3>
          [LOCALHOST ONLY: {currentQuestion?.dbId}{' '}
          {currentQuestion?.questionType}]
        </h3>
      )}
      {content}
      {section !== 'Beneficiaries' &&
        section !== 'Impact Model Details' &&
        section !== 'Budget' &&
        section !== 'Narrative' &&
        section !== 'Program Overview' &&
        section !== 'Final Results' &&
        section !== 'Interim Forecasts' &&
        section !== 'Progress Notes' &&
        section !== 'Review & Submit' &&
        currentQuestion?.dbId !== DATES_QUESTION_ID_1 &&
        currentQuestion?.dbId !== 40000 &&
        currentQuestion?.dbId !== 40015 &&
        !NOTE_QUESTION_ID_LIST.includes(currentQuestion?.dbId) && (
          <BottomBar
            hideBack={currentQuestion?.sectionStart && section === 'allocation'}
            hideNext={autoSave || !choiceIsSelected}
            handleBack={handleBack}
            handleNext={() => handleNext(true)}
            backTitle={backTitle}
            nextTitle={nextTitle}
          />
        )}
    </DonorNameFlagProvider>
  );
});
